import { FeatureFlag } from '@doordash/lib-feature-flags'
import { get } from 'lodash'
import * as React from 'react'
import { connect } from 'react-redux'
import { RouteComponentProps, withRouter } from 'react-router'
import * as actions from '../../../actions'
import { push, Steps } from '../../../utilities/steps'
import { applicationCountrySelector } from '../../../selectors'
import * as events from '../../../events'
import * as api from '../../../api'
import { Spacing, StackChildren } from '@doordash/design-language'
import Australia from './australia'
import Canada from './canada'
import PuertoRico from './puerto-rico'
import { PR_MARKET_ID } from '../../../constants'
import { VehiclePatchData } from '../../../models/types'
import USA, { isTokenizationEnabled } from './usa'
import { VGSForm } from '../../../hooks/useVGS'
import Japan from './japan'
import { sleep } from '../../../utilities/sleep'

type Props = {
  country: string
  uniqueLink: string
  application?: any
  dasherId?: number
  experiments?: object
  requestedBgCheckCopy?: boolean
  submitBackgroundCheck?: (
    uniqueLink: string,
    backgroundInfo: object,
    requestedBgCheckCopy?: boolean
  ) => Promise<any>
  submitBackgroundCheckTokenized?: (
    uniqueLink: string,
    backgroundInfo: object,
    form?: VGSForm
  ) => Promise<any>
  updateDasherApplicant?: (string, any) => Promise<any>
  createDasherAccount?: (string) => Promise<any>
  loadApplication?: (string) => Promise<any>
  loadApplicationAndCreatePaymentAccount?: (string, object) => Promise<any>
  trackBackgroundCheckFormRendered?: (string) => Promise<any>
  trackSubmitBackgroundCheck?: (string) => Promise<any>
  updateVehicle?: (vehicleId: number, data: VehiclePatchData) => Promise<any>
  selectedApplicationCountry?: { [k: string]: boolean }
}

type State = {
  backgroundInfo: any
}
export class BackgroundCheck extends React.Component<
  Props & RouteComponentProps<any>,
  State
> {
  public static defaultProps: Props = {
    country: 'US',
    uniqueLink: null,
    application: null,
    dasherId: null,
    experiments: null,
    requestedBgCheckCopy: null,
    submitBackgroundCheck: () => Promise.resolve(console.log),
    submitBackgroundCheckTokenized: () => Promise.resolve(console.log),
    updateDasherApplicant: () => Promise.resolve(console.log),
    createDasherAccount: () => Promise.resolve(console.log),
    loadApplication: () => Promise.resolve(console.log),
    trackBackgroundCheckFormRendered: () => Promise.resolve(console.log),
    trackSubmitBackgroundCheck: () => Promise.resolve(console.log),
    updateVehicle: () => Promise.resolve(console.log),
    selectedApplicationCountry: {},
  }

  public componentDidMount() {
    const { application, trackBackgroundCheckFormRendered } = this.props
    trackBackgroundCheckFormRendered(application.country)
    // getStateCode(zipcode, application.country)
  }

  public submitBackgroundCheck = async (uniqueLink, backgroundInfo, form?) => {
    const {
      application,
      updateDasherApplicant,
      createDasherAccount,
      submitBackgroundCheck,
      submitBackgroundCheckTokenized,
      dasherId,
      history,
      loadApplication,
      loadApplicationAndCreatePaymentAccount,
      trackSubmitBackgroundCheck,
      updateVehicle,
      selectedApplicationCountry,
    } = this.props
    // Ready payload by removing empty keys
    Object.keys(backgroundInfo).forEach(
      key => backgroundInfo[key].length === 0 && delete backgroundInfo[key]
    )

    trackSubmitBackgroundCheck(application)

    const isPR = application.market === PR_MARKET_ID
    const isCA = application.country === 'CA'

    await updateDasherApplicant(uniqueLink, backgroundInfo)
    await sleep(500)
    if (typeof dasherId === 'undefined' || dasherId === null) {
      await createDasherAccount(uniqueLink)
      await sleep(500)
    }

    if (isTokenizationEnabled()) {
      await submitBackgroundCheckTokenized(uniqueLink, backgroundInfo, form)
    } else if (!isCA && !isPR) {
      await submitBackgroundCheck(uniqueLink, backgroundInfo)
    }

    // Reload application object after successful background check submit
    const bankOnboardingExperiment = get(
      this.props.experiments,
      'bank_onboarding',
      false
    )

    if (bankOnboardingExperiment && !selectedApplicationCountry.isJP) {
      await loadApplicationAndCreatePaymentAccount(uniqueLink, backgroundInfo)
    } else {
      await loadApplication(uniqueLink)
    }

    // TODO(@markn): make this better
    try {
      const res = await api.getApplicationStatus(uniqueLink)
      const vehicleId = get(res, 'data.vehicleId')
      if (vehicleId) {
        const { make, model, color } = backgroundInfo
        await updateVehicle(vehicleId, {
          make,
          model,
          color,
        })
      }
    } catch (e) {
      // some err
    }

    push(uniqueLink, Steps.BackgroundCheckAwait, history)
  }

  public render() {
    const {
      country,
      application,
      match: {
        params: { uniqueLink },
      },
      selectedApplicationCountry,
    } = this.props

    const isPR = application.market === PR_MARKET_ID
    const isUS = country === 'US' && !isPR
    return (
      <StackChildren size={Spacing.Large}>
        {isUS && (
          <USA
            {...this.props}
            doSubmit={this.submitBackgroundCheck}
            uniqueLink={uniqueLink}
          />
        )}
        {country === 'CA' && (
          <FeatureFlag
            on={() => null}
            off={() => (
              <Canada
                {...this.props}
                doSubmit={this.submitBackgroundCheck}
                uniqueLink={uniqueLink}
              />
            )}
            flagName="canadaBgcRedirect"
          />
        )}
        {country === 'AU' && (
          <Australia
            {...this.props}
            doSubmit={this.submitBackgroundCheck}
            uniqueLink={uniqueLink}
          />
        )}
        {isPR && (
          <PuertoRico
            {...this.props}
            doSubmit={this.submitBackgroundCheck}
            uniqueLink={uniqueLink}
          />
        )}
        {selectedApplicationCountry.isJP && (
          <Japan
            {...this.props}
            doSubmit={this.submitBackgroundCheck}
            uniqueLink={uniqueLink}
          />
        )}
      </StackChildren>
    )
  }
}

const mapStateToProps = state => ({
  application: state.application,
  isLoggedIn: state.isLoggedIn,
  vehicleTypes: state.vehicleTypes,
  zipcode: state.zipcode,
  dasherId: state.dasherId,
  experiments: state.experiments,
  uniqueLink: state.uniqueLink,
  error: state.errorMessage,
  selectedApplicationCountry: applicationCountrySelector(state),
})

const mapDispatchToProps = {
  submitBackgroundCheck: actions.submitBackgroundCheck,
  submitBackgroundCheckTokenized: actions.submitBackgroundCheckTokenized,
  updateDasherApplicant: actions.updateDasherApplicant,
  createDasherAccount: actions.createDasherAccount,
  updateVehicle: actions.updateVehicle,
  loadApplication: actions.loadApplication,
  loadApplicationAndCreatePaymentAccount:
    actions.loadApplicationAndCreatePaymentAccount,
  trackBackgroundCheckFormRendered: events.trackBackgroundCheckFormRendered,
  trackSubmitBackgroundCheck: events.trackSubmitBackgroundCheck,
}

export default withRouter<Props & RouteComponentProps<any>>(
  connect(mapStateToProps, mapDispatchToProps)(BackgroundCheck)
)
